import { useCallback, useEffect, useId, useRef, useState, type DragEvent } from 'react';
import { Camera, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { PendingDecision } from '@/components/editorial/PendingDecision';
import type { PendingDecisionId } from '@/lib/pending-decisions';
import { api } from '@/services/api';
import { localizedApiErrorMessage } from '@/lib/utils';
import { MAX_UPLOAD_MB, UPLOAD_ACCEPT, isUnsupportedFileTypeError, uploadRefusal } from '@/lib/uploadTypes';
import { formatUsd, scanFailed } from '@/lib/bills-display';
import type { ReimbursementClaim } from '@/types/api';

/**
 * Capture first, fields later (Finances collector spec 2026-09-10 §4.4).
 *
 * Drop a receipt, pick a file, or — on a phone — open the camera. Each file
 * uploads at once with no fields; the ledger shows the new row as
 * "Scanning…" and fills it in when the extraction lands. Several files make
 * several rows. Nothing here asks the parent to type what the receipt says.
 *
 * #2378 (option A): each file's line follows its own claim until the scan
 * settles, then says what happened — "GREENLEAF GROCERY · $33.00 — sent to
 * TRF for review", "… add the missing details", or "We couldn't read it" —
 * with a See it link to the receipt. Before, the line read "Added — reading
 * it now" for good while the scan finished in seconds and auto-submitted the
 * receipt into the folded "Past receipts", out of sight.
 */
export interface ReceiptCaptureProps {
  /** Called after each successful upload, and again when its scan settles, so the ledger can refetch. */
  onUploaded: () => void;
  /** Show the receipt on the page (the line's See it link). Omitted, the line offers no link. */
  onOpenClaim?: (claim: ReimbursementClaim) => void;
  onClose?: () => void;
  className?: string;
}

interface Pending {
  key: number;
  name: string;
  state: 'uploading' | 'reading' | 'resolved' | 'failed';
  error?: string;
  claimId?: string;
  /** The claim as last read; set once the line resolves (absent if it never could be read back). */
  claim?: ReimbursementClaim;
  startedAt?: number;
}

/**
 * #2380: the picker offers exactly what the server takes (JPEG, PNG, PDF —
 * `lib/uploadTypes`, mirrored from documents.js). Drag and drop and "All
 * files" still reach `uploadFiles`, so each file is also checked there.
 */
const ACCEPT = UPLOAD_ACCEPT;

/** How often a reading line asks after its claim (the ledger's own cadence). */
const POLL_MS = 3_000;
/**
 * How long a line waits for the scan before it stops asking (#2378). The
 * 2026-10-08 runs read in 4 s (en), 20 s (es) and 40 s (zh); three minutes
 * leaves room for a slow scan without polling forever.
 */
const RESOLVE_WINDOW_MS = 3 * 60_000;

/** The scan has settled: it read the receipt, failed to, or the claim moved on. */
function scanSettled(claim: ReimbursementClaim): boolean {
  return claim.status !== 'draft' || Boolean(claim.ocr_extracted?.extracted_at) || Boolean(claim.ocr_extracted?.error);
}

export function ReceiptCapture({ onUploaded, onOpenClaim, onClose, className }: ReceiptCaptureProps) {
  const { t, i18n } = useTranslation(['money', 'common']);
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [pending, setPending] = useState<Pending[]>([]);
  const idBase = useId();
  const nextKey = useRef(0);
  const onUploadedRef = useRef(onUploaded);
  useEffect(() => { onUploadedRef.current = onUploaded; }, [onUploaded]);

  const update = (key: number, patch: Partial<Pending>) =>
    setPending((p) => p.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const uploadFiles = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    for (const file of list) {
      const key = nextKey.current++;
      // #2380: refuse before the request, in the reader's language. A file
      // the server would refuse can never pass, so "try again" is wrong.
      const refusal = uploadRefusal(file);
      if (refusal) {
        const error = refusal === 'type' ? t('capture.wrongType') : t('capture.tooLarge', { size: MAX_UPLOAD_MB });
        setPending((p) => [...p, { key, name: file.name, state: 'failed', error }]);
        continue;
      }
      setPending((p) => [...p, { key, name: file.name, state: 'uploading' }]);
      const form = new FormData();
      form.append('document', file);
      form.append('kind', 'receipt');
      try {
        const res = await api.documents.upload(form);
        const claimId = res?.data?.claim?._id;
        // No claim to follow (not a receipt branch): nothing will settle, so
        // the line points to the receipts rather than reading forever.
        update(key, claimId
          ? { state: 'reading', claimId, startedAt: Date.now() }
          : { state: 'resolved' });
        onUploadedRef.current();
      } catch (err) {
        // #2380: a coded refusal is named in every language. Otherwise
        // R3-B11: the server's English only for an English reader.
        const english = i18n.language === 'en' || i18n.language.startsWith('en-');
        const message = isUnsupportedFileTypeError(err)
          ? t('capture.wrongType')
          : english
          ? (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
            || (err as Error)?.message || t('capture.uploadFailed')
          : localizedApiErrorMessage(err, t, i18n.language) || t('capture.uploadFailed');
        update(key, { state: 'failed', error: message });
      }
    }
  }, [t, i18n]);

  // #2378: follow every reading line to a settled claim. One loop for all of
  // them; it stops when none is reading. A failed read is not a verdict — the
  // line keeps asking until its window closes.
  const reading = pending.filter((x) => x.state === 'reading');
  const readingKey = reading.map((x) => x.key).join(',');
  useEffect(() => {
    if (!readingKey) return;
    let cancelled = false;
    const tick = async () => {
      const now = Date.now();
      const settled: Array<{ key: number; claim?: ReimbursementClaim }> = [];
      await Promise.all(reading.map(async (x) => {
        let claim: ReimbursementClaim | undefined;
        try {
          const res = await api.reimbursements.get(x.claimId as string);
          claim = (res?.data ?? undefined) as ReimbursementClaim | undefined;
        } catch {
          claim = undefined;
        }
        if (claim && scanSettled(claim)) settled.push({ key: x.key, claim });
        else if (now - (x.startedAt ?? now) >= RESOLVE_WINDOW_MS) settled.push({ key: x.key, claim: undefined });
      }));
      if (cancelled || settled.length === 0) return;
      setPending((p) => p.map((x) => {
        const hit = settled.find((s) => s.key === x.key);
        return hit ? { ...x, state: 'resolved', claim: hit.claim } : x;
      }));
      // The ledger polls only while a row is scanning, and a slow scan can
      // outlive its window; ask it once more so the row matches this line.
      onUploadedRef.current();
    };
    void tick();
    const timer = setInterval(() => { void tick(); }, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
    // `reading` is derived from `pending`; the key list is its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readingKey]);

  /** The settled line for one file, in the reader's language. */
  const resolvedText = (x: Pending): string => {
    const claim = x.claim;
    if (!claim) return t('capture.inReceipts');
    const amount = claim.amount != null && Number(claim.amount) > 0 ? formatUsd(Number(claim.amount)) : null;
    const what = [claim.vendor, amount].filter(Boolean).join(' · ') || x.name;
    if (claim.status !== 'draft') return t('capture.sent', { what });
    if (scanFailed(claim)) return t('capture.unreadable');
    return t('capture.needsDetails', { what });
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) void uploadFiles(e.dataTransfer.files);
  };

  const lineDecisions: PendingDecisionId[] = [];
  if (pending.some((p) => p.state !== 'failed')) lineDecisions.push('receipt-capture-follow-up');
  if (pending.some((p) => p.state === 'failed' && p.error === t('capture.wrongType'))) lineDecisions.push('receipt-refused-type-wording');

  return (
    <div
      data-testid="receipt-capture"
      className={cn(
        'rounded-lg border-2 border-dashed p-5 text-center transition-colors',
        dragging ? 'border-stevens-indigo bg-stevens-indigo/5' : 'border-border/70 bg-card/40',
        className,
      )}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <p className="text-body font-medium">{t('capture.title')}</p>
      <p className="mt-1 text-caption text-stevens-ink-3">
        {t('capture.body')}
      </p>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <Button type="button" size="sm" variant="brand" onClick={() => inputRef.current?.click()}>
          <Upload className="mr-1.5 h-3.5 w-3.5" />
          {t('capture.choose')}
        </Button>
        {/* `capture` opens the camera on phones; desktops fall back to the picker. */}
        <Button type="button" size="sm" variant="secondary" className="sm:hidden" onClick={() => cameraRef.current?.click()}>
          <Camera className="mr-1.5 h-3.5 w-3.5" />
          {t('capture.photo')}
        </Button>
        {onClose && (
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
        )}
      </div>
      <input
        ref={inputRef}
        data-testid="receipt-file-input"
        type="file"
        accept={ACCEPT}
        multiple
        className="sr-only"
        aria-label={t('capture.fileInput')}
        onChange={(e) => { if (e.target.files) void uploadFiles(e.target.files); e.target.value = ''; }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-label={t('capture.cameraInput')}
        onChange={(e) => { if (e.target.files) void uploadFiles(e.target.files); e.target.value = ''; }}
      />
      {/* #2378: the lines change while the parent waits; the region is on the
          page before the first upload so a screen reader announces each change. */}
      <div role="status" aria-live="polite">
      {pending.length > 0 && (
        <ul className="mt-3 space-y-1 text-left text-caption" data-testid="receipt-capture-progress">
          {pending.map((p) => (
            <li key={p.key} className="flex items-center justify-between gap-2" data-state={p.state}>
              <span className="truncate" id={`${idBase}-name-${p.key}`}>{p.name}</span>
              <span className={cn(
                'flex min-w-0 items-center gap-2 text-end',
                p.state === 'failed' ? 'text-status-danger-fg' : 'text-stevens-ink-3',
              )}>
                <span className="break-words">
                  {p.state === 'uploading' ? t('capture.uploading')
                    : p.state === 'reading' ? t('capture.added')
                    : p.state === 'resolved' ? resolvedText(p)
                    : p.error}
                </span>
                {p.state === 'resolved' && p.claim && onOpenClaim && (
                  <button
                    type="button"
                    id={`${idBase}-see-${p.key}`}
                    // "See it" plus the file's name, so several lines' links are told apart.
                    aria-labelledby={`${idBase}-see-${p.key} ${idBase}-name-${p.key}`}
                    className="shrink-0 text-stevens-indigo hover:underline underline-offset-2"
                    onClick={() => onOpenClaim(p.claim as ReimbursementClaim)}
                  >
                    {t('capture.seeIt')}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      </div>
      {/* Wave 2's two unruled choices about these lines (#2378 how long a
          line follows its receipt, #2380 the refused-type sentence), once,
          under the list and outside the live region so it is not announced. */}
      {lineDecisions.length > 0 && (
        <div className="mt-1 flex justify-end">
          <PendingDecision id={lineDecisions} side="top" />
        </div>
      )}
    </div>
  );
}
